<template>
  <div
    v-if="enableSticky"
    ref="stickySentinel"
    class="h-0"
  >
    <!-- Sentinel element for sticky detection (only when sticky enabled) -->
  </div>

  <div
    ref="tabsContainer"
    class="flex flex-col px-4 sm:px-0 2xl:px-0 @container am3-tabs-container"
    :class="[
      enableSticky ? 'sticky bg-surface top-0 z-30 pt-6 pb-[calc(1.5rem-1px)] [&_+_div]:mt-px' : 'pb-6',
      enableSticky && isSticky ? stickyClasses : '',
    ]"
  >
    <div
      :class="
        [nested ? 'am3-tabs-header--nested' : 'am3-tabs-header @sm:am3-container']
      "
      class="flex relative  justify-between "
    >
      <div class="flex min-w-max overflow-x-auto">
        <a
          v-for="tab in tabs"
          :key="tab.key"
          :ref="(el) => { if (el) tabRefs[tab.key] = el as HTMLElement }"
          :data-key="tab.key"
          :href="syncWithUrl ? `#${tab.key}` : undefined"
          class="px-3 py-2 cursor-pointer text-sm relative z-10 ml-px font-medium my-p transition-all duration-75 flex items-center gap-2 outline outline-1 outline-transparent active:rounded-none rounded-md active:bg-transparent  focus:hover:bg-transparent focus-within:hover:bg-transparent no-underline"
          :class="[
            modelValue === tab.key
              ? 'text-brand'
              : 'text-secondary hover:text-brand',
          ]"
          :data-test-id="generateDataTestId(dataTestId, 'tab', tab.key)"
          :aria-label="`Select tab - ${tab.title}`"
          @click="handleTabClick(tab.key, $event)"
        >
          <AM3Icon
            v-if="tab.icon"
            :type="tab.icon"
            class="w-4 h-4"
          />
          {{ tab.title }}
        </a>
        <!-- Animated underline -->
        <div
          class="absolute bottom-0 h-0.5 bg-brand transition-all duration-75 ease-in-out"
          :style="underlineStyle"
        />
      </div>
      <slot name="button" />
    </div>
    <!-- Before content slot positioned between tabs and content -->
    <slot name="before-content" />
    <div :class="modelValue ? 'has-[div]:py-4' : '' ">
      <!-- main tab content -->
      <slot :name="modelValue || 'default'" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

interface Tab {
  key: string
  title: string
  icon?: string
}

interface Props {
  modelValue: string
  tabs: Tab[]
  dataTestId?: string
  enableSticky?: boolean
  stickyClasses?: string
  syncWithUrl?: boolean
  nested?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  dataTestId: 'tabs',
  enableSticky: false,
  stickyClasses: 'shadow-sm',
  syncWithUrl: false,
  nested: false,
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

// Create a reactive object to store tab element references
const tabRefs = ref<Record<string, HTMLElement>>({})

// Sticky functionality refs
const tabsContainer = ref<HTMLElement>()
const stickySentinel = ref<HTMLElement>()
const isSticky = ref(false)
let stickyObserver: IntersectionObserver | null = null

const handleTabClick = (key: string, event: Event) => {
  // If using URL sync, let native navigation handle it
  // Otherwise, prevent default and emit directly
  if (!props.syncWithUrl) {
    event.preventDefault()
    emit('update:modelValue', key)
  }
  // When syncWithUrl is true, the browser navigates to the hash
  // and hashchange event will handle the update
}

// Use the ref object to get selected tab dimensions
const getSelectedTabPosition = (): number => {
  return tabRefs.value[props.modelValue]?.offsetLeft || 0
}

const getSelectedTabWidth = (): number => {
  return tabRefs.value[props.modelValue]?.offsetWidth || 0
}

const underlineStyle = computed(() => ({
  left: `calc( ${getSelectedTabPosition()}px + .625rem)`,
  width: ` calc( ${getSelectedTabWidth()}px - 1.25rem )`,
  height: `2px`,
  bottom: '0',
  zIndex: '11',
}))

// Update tab positions when modelValue changes or tabs are added/removed
watch(() => props.modelValue, () => {
  // The tabRefs should already be updated, and the computed style will recompute
})

// Also watch for tab changes in case the whole tabs array is replaced
watch(() => props.tabs, () => {
  // The refs will be regenerated and the computed style will update
}, { deep: true })

// URL sync functionality - simple hashchange listener
const getTabFromHash = (): string | null => {
  if (typeof window === 'undefined' || !props.syncWithUrl) return null

  const hash = window.location.hash.substring(1).split('?')[0]
  const validTabKeys = props.tabs.map(tab => tab.key)

  return validTabKeys.includes(hash) ? hash : null
}

const handleHashChange = () => {
  if (!props.syncWithUrl) return

  const newTab = getTabFromHash()
  if (newTab && newTab !== props.modelValue) {
    emit('update:modelValue', newTab)
  }
}

// Sticky functionality
const setupStickyObserver = () => {
  if (!props.enableSticky || !stickySentinel.value || typeof window === 'undefined') return

  stickyObserver = new IntersectionObserver(
    ([entry]) => {
      // When sentinel is not intersecting (not visible), the sticky element is stuck
      isSticky.value = !entry.isIntersecting
    },
    {
      rootMargin: '0px',
      threshold: 0,
    },
  )

  stickyObserver.observe(stickySentinel.value)
}

const cleanupStickyObserver = () => {
  if (stickyObserver) {
    stickyObserver.disconnect()
    stickyObserver = null
  }
}

// Setup on mount
onMounted(() => {
  if (props.enableSticky) {
    nextTick(() => {
      setupStickyObserver()
    })
  }

  // Initialize URL sync with native hash navigation
  if (props.syncWithUrl && typeof window !== 'undefined') {
    const hashTab = getTabFromHash()

    // If there's a valid hash in URL, sync to it
    if (hashTab && hashTab !== props.modelValue) {
      emit('update:modelValue', hashTab)
    }
    // If there's no hash, set initial hash without adding to history
    else if (!window.location.hash && props.modelValue) {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}#${props.modelValue}`,
      )
    }

    // Listen for hash changes (back/forward navigation)
    window.addEventListener('hashchange', handleHashChange)
  }
})

// Cleanup on unmount
onBeforeUnmount(() => {
  cleanupStickyObserver()

  if (props.syncWithUrl && typeof window !== 'undefined') {
    window.removeEventListener('hashchange', handleHashChange)
  }
})

// Clear the tab hash only on a real route change - not on every component
// unmount. Doing this in onBeforeUnmount instead wiped the hash (and with it,
// the active tab) whenever this component was torn down without an actual
// navigation - e.g. Vite HMR re-mounting it during dev, or a parent `v-if`
// toggling it off and back on.
onBeforeRouteLeave(() => {
  if (props.syncWithUrl && typeof window !== 'undefined' && window.location.hash) {
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + window.location.search,
    )
  }
})
</script>
